<template>
  <div class="position-relative">
    <div class="widget">
      <div class="widget-content" v-if="importing">
        <AlertBlock :text="$t('training.program.alert.importing')" type="success mb-0" />
      </div>
      <template v-if="hasBeenImportedOnce">
        <div class="widget-heading">
          <h2 class="textStyle">
            <FontAwesomeIcon icon="fa-light fa-books" />
            {{ $t("training.program.model") }}
          </h2>
          <div class="bloc-actions">
            <span class="badge bg-info" v-if="trainingPeriods"
              >{{ Object.keys(trainingPeriods).length }}
              {{
                $tc(
                  "studea_manager.trainingPeriod.title",
                  Object.keys(trainingPeriods).length > 1 ? 2 : 1,
                ).toLowerCase()
              }}</span
            >
            <button
              class="btn btn-icon-text ms-md-2 ms-0 btn-add addTrainingPeriod"
              type="button"
              @click="addEditTrainingPeriod"
              v-tooltip="{
                title: stringManager().globalTranslation('add', 'training-period', 'studea_manager', 1, true),
              }"
            >
              <FontAwesomeIcon icon="fa-light fa-plus" />
              {{ $tc("studea_manager.trainingPeriod.title", 1) }}
            </button>
            <button
              class="btn btn-icon-text btn-add ms-md-2 ms-0 addUnit"
              type="button"
              @click="addUnit"
              v-tooltip="{
                title: stringManager().globalTranslation('add', 'unit', 'studea_manager', 1, true),
              }"
            >
              <FontAwesomeIcon icon="fa-light fa-plus" />
              {{ $tc("studea_manager.unit.title", 1) }}
            </button>
            <button
              class="btn btn-icon-text btn-add ms-md-2 ms-0 addModule"
              type="button"
              @click="addModule"
              v-tooltip="{
                title: stringManager().globalTranslation('add', 'module', 'studea_manager', 1, true),
              }"
            >
              <FontAwesomeIcon icon="fa-light fa-plus" />
              {{ $tc("studea_manager.module.title", 1) }}
            </button>
          </div>
        </div>
        <div class="widget-content">
          <AlertBlock
            v-if="warnings"
            :title="$t('training.program.alert.warning_points')"
            :text="warnings"
            type="warning"
            icon="warning"
          />
          <div id="skill-category-accordion" class="accordion accordion-icons">
            <template v-if="trainingPeriods && Object.keys(trainingPeriods).length">
              <template v-for="(trainingPeriod, index) in trainingPeriods" :key="trainingPeriod.id">
                <TrainingTrainingPeriodComponent
                  :trainingPeriod="trainingPeriod"
                  :show="index === 0"
                  @editTrainingPeriod="addEditTrainingPeriod"
                  @editUnitSubject="addEditUnitSubject"
                  @removed="removed"
                  @hideComplete="hideComplete"
                />
              </template>
            </template>
            <AlertBlock v-else-if="!importing" :text="$t('training.program.alert.empty_semester')" type="info" />
          </div>
        </div>
      </template>
      <div class="widget-content" v-else-if="!importing">
        <AlertBlock :text="$t('training.program.alert.program_import')" type="info mb-0" />
      </div>
    </div>
    <BlockLoader :loading="loading || importing" />
  </div>
  <ModalFormComponent
    :alertTitle="modalAlertTitle"
    :alert="modalAlertContent"
    alertIcon="warning"
    alertType="warning"
    :models="models"
    :title="modalTitle"
    @submit="modalSubmit"
  />
</template>

<script>
import BlockLoader from "@/components/blocks/BlockLoader.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { trainingManager } from "@/services/training/training-manager";
import ModalFormComponent from "@/components/form/ModalFormComponent.vue";
import { formManager } from "@/services/form/form-manager";
import { useNotificationStore } from "@/store/notifications/notification";
import { mapState } from "pinia";
import AlertBlock from "@/components/blocks/AlertBlock.vue";
import { notificationManager } from "@/services/utilities/notification-manager";
import { i18n } from "@/i18n";
import { stringManager } from "@/services/utilities/string-manager";
import TrainingTrainingPeriodComponent from "@/components/training/model/TrainingTrainingPeriodComponent.vue";

export default {
  name: "TrainingModelComponent",
  components: {
    TrainingTrainingPeriodComponent,
    AlertBlock,
    ModalFormComponent,
    FontAwesomeIcon,
    BlockLoader,
  },
  emits: ["refresh", "error"],
  props: {
    hasBeenImportedOnce: {
      type: Boolean,
      required: true,
    },
    importing: {
      type: Boolean,
      required: true,
    },
    active: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      models: [],
      modalTitle: "",
      modalType: "",
      modalAlertTitle: null,
      modalAlertContent: null,
      modalTrainingPeriodId: null,
      modalParentUnitId: null,
      modalObjectId: null,
      modalObject: null,
      modalLoadObject: null,
      targetChoices: [],
      trainingPeriods: [],
    };
  },
  watch: {
    status() {
      this.checkMessagesState(this.messages, true);
    },

    $route() {
      this.init();
    },

    active() {
      this.init();
    },
  },
  computed: {
    warnings() {
      let unitMissingCodes = 0;
      let unitNeedValidation = 0;
      let subjectMissingCodes = 0;
      let subjectNeedValidation = 0;

      if (this.trainingPeriods && Object.keys(this.trainingPeriods).length) {
        Object.values(this.trainingPeriods).forEach((trainingPeriod) => {
          if (trainingPeriod.trainingUnits && Object.keys(trainingPeriod.trainingUnits).length) {
            Object.values(trainingPeriod.trainingUnits).forEach((unit) => {
              if (!unit.code) {
                unitMissingCodes++;
              }
              if (unit.confidenceScore && unit.confidenceScore < 75) {
                unitNeedValidation++;
              }
              if (unit.trainingSubjects && Object.keys(unit.trainingSubjects).length) {
                Object.values(unit.trainingSubjects).forEach((subject) => {
                  if (!subject.code) {
                    subjectMissingCodes++;
                  }
                  if (subject.confidenceScore && subject.confidenceScore < 75) {
                    subjectNeedValidation++;
                  }
                });
              }
              if (unit.childTrainingUnits && Object.keys(unit.childTrainingUnits).length) {
                Object.values(unit.childTrainingUnits).forEach((childUnit) => {
                  if (!childUnit.code) {
                    unitMissingCodes++;
                  }
                  if (childUnit.confidenceScore && childUnit.confidenceScore < 75) {
                    unitNeedValidation++;
                  }
                });
              }
            });
          }
        });
      }

      let warnings = "";
      let needSeparator = false;
      if (unitMissingCodes) {
        warnings = warnings.concat(
          i18n.global.tc("training.program.alert.unit_missing_code", unitMissingCodes, { count: unitMissingCodes }),
        );
        needSeparator = true;
      }
      if (subjectMissingCodes) {
        if (needSeparator) {
          warnings = warnings.concat(" • ");
        }
        warnings = warnings.concat(
          i18n.global.tc("training.program.alert.subject_missing_code", subjectMissingCodes, {
            count: subjectMissingCodes,
          }),
        );
        needSeparator = true;
      }
      if (unitNeedValidation) {
        if (needSeparator) {
          warnings = warnings.concat(" • ");
        }
        warnings = warnings.concat(
          i18n.global.tc("training.program.alert.unit_need_validation", unitNeedValidation, {
            count: unitNeedValidation,
          }),
        );
        needSeparator = true;
      }
      if (subjectNeedValidation) {
        if (needSeparator) {
          warnings = warnings.concat(" • ");
        }
        warnings = warnings.concat(
          i18n.global.tc("training.program.alert.subject_need_validation", subjectNeedValidation, {
            count: subjectNeedValidation,
          }),
        );
        needSeparator = true;
      }

      return warnings;
    },

    ...mapState(useNotificationStore, {
      status: (store) => store.status,
      messages: (store) => store.messages,
    }),
  },
  mounted() {
    this.init();
  },
  methods: {
    stringManager() {
      return stringManager;
    },

    init() {
      if (this.active) {
        this.getData();
        this.checkMessagesState(this.messages);
      }
    },

    checkMessagesState(messages, needInit = false) {
      let messageId;
      if (sessionStorage.getItem("studea-training-" + this.$route.params.trainingId + "-programImportMessageId")) {
        messageId = sessionStorage.getItem(
          "studea-training-" + this.$route.params.trainingId + "-programImportMessageId",
        );
      }
      const message = Object.values(messages).find((message) => message.id === messageId);
      if (message) {
        if (message.status === "success") {
          this.$emit("refresh");
          if (needInit) {
            this.init();
          }
        } else if (message.status === "error") {
          this.$emit("error");
        }
      }
    },

    clearData() {
      this.trainingPeriods = {};
    },

    addUnit() {
      this.addEditUnitSubject({ type: "unit" });
    },

    addModule() {
      this.addEditUnitSubject({ type: "subject" });
    },

    addEditTrainingPeriod(payload) {
      this.clearModal();
      this.modalType = "training-period";
      this.modalTitle = stringManager.globalTranslation(
        payload.trainingPeriod?.id ? "edit" : "add",
        "training-period",
        "studea_manager",
        1,
        true,
      );
      this.modalObjectId = payload.trainingPeriod?.id;
      this.modalObject = payload.trainingPeriod;
      this.modalLoadObject = payload.trainingPeriod;
      this.form();
    },

    addEditUnitSubject(payload) {
      this.clearModal();
      this.modalType = payload.type;
      this.modalTitle = stringManager.globalTranslation(
        payload.trainingPeriod?.id ? "edit" : "add",
        this.modalType,
        "studea_manager",
        1,
        true,
      );
      this.modalObjectId = payload.object?.id;
      this.modalObject = payload.object;
      this.modalLoadObject = payload.object;
      this.modalTrainingPeriodId = payload.trainingPeriod?.id;
      this.modalParentUnitId = payload.parent?.id;
      this.form();
    },

    async form() {
      this.models = [];
      this.setLoading();
      if (this.modalObject && this.modalObject.confidenceScore && this.modalObject.confidenceScore < 75) {
        this.modalAlertTitle = i18n.global.t("training.program.alert.verification");
        this.modalAlertContent = i18n.global.t("training.program.alert.low_confidence_score", {
          confidence: this.modalObject.confidenceScore,
        });
      }
      this.models = await trainingManager.form(
        this.modalType,
        this.modalObjectId,
        false,
        this.$route.params.trainingId,
      );
      this.cancelLoading();
    },

    sort(e) {
      if (e.moved) {
        const id = e.moved.element.id;
        const position = e.moved.newIndex;
        trainingManager.sort(id, this.$route.params.trainingId, position, "training-period", "training-period");
      }
    },

    async getData() {
      this.loading = true;
      this.trainingPeriods = await trainingManager.get(this.$route.params.trainingId, "unit/list");
      this.loading = false;
    },

    setLoading() {
      if (this.modalType === "training-period" && !this.modalObjectId) {
        this.loading = true;
      }
      if (this.modalLoadObject) {
        this.modalLoadObject["loading"] = true;
      }
    },

    cancelLoading() {
      if (this.modalLoadObject) {
        this.modalLoadObject["loading"] = false;
      }
      this.loading = false;
    },

    async modalSubmit() {
      const params = formManager.processForm(this.models);
      if (this.modalTrainingPeriodId && !this.modalObjectId) {
        {
          params["trainingPeriod"] = this.modalTrainingPeriodId;
        }
      }

      this.setLoading();
      const edit = !!this.modalObjectId;

      const response = await (edit
        ? trainingManager.put(this.modalObjectId, this.$route.params.trainingId, this.modalType, params)
        : trainingManager.post(this.$route.params.trainingId, this.modalType, params));

      const error = response.hasError || (response.success !== undefined && !response.success);

      if (!error && this.modalObject) {
        this.updateObjectWithResponse(response);
      } else if (!error) {
        this.objectPush(response);
      }

      const tradPrefix = stringManager.toCamelCase(this.modalType);
      const tradGender = stringManager.gender(tradPrefix);
      const message = response?.response?.data?.message;

      notificationManager.showNotification(
        !error ? "success" : "error",
        !error
          ? i18n.global.tc("global." + (edit ? "edit" : "add") + ".success", tradGender, {
              name: i18n.global.tc("studea_manager." + tradPrefix + ".title", 1),
            })
          : message
            ? message
            : i18n.global.t("signed_error"),
      );
      this.cancelLoading();
      this.clearModal();
    },

    updateObjectWithResponse(response) {
      const responseObject = this.getResponseObjet(response);
      if (responseObject) {
        this.getKeysToUpdate().forEach((key) => {
          if (["trainingPeriod", "parentTrainingUnit", "trainingUnit"].includes(key)) {
            if (key === "trainingPeriod" && !responseObject.parentTrainingUnit) {
              this.moveToTrainingPeriod(responseObject);
            } else if (key === "parentTrainingUnit") {
              this.moveTrainingUnit(responseObject);
            } else if (key === "trainingUnit") {
              this.moveSubject(responseObject);
            }
          } else {
            this.modalObject[key] = response.responses[0].object[key];
          }
        });
      }
    },

    getResponseObjet(response) {
      return response?.responses[0]?.object;
    },

    moveTrainingUnit(responseObject) {
      const targetTrainingPeriod = this.getTrainingPeriod(responseObject.trainingPeriod?.id);
      const targetTrainingUnit = this.getTrainingUnit(targetTrainingPeriod, responseObject.parentTrainingUnit?.id);
      const oldTrainingPeriod = this.getTrainingPeriod(this.modalTrainingPeriodId);
      const oldTrainingUnit = this.getTrainingUnit(oldTrainingPeriod, this.modalParentUnitId);
      if (!targetTrainingUnit || !oldTrainingUnit || targetTrainingUnit.id !== oldTrainingUnit.id) {
        if (oldTrainingUnit && oldTrainingUnit.id !== targetTrainingUnit?.id) {
          oldTrainingUnit.childTrainingUnits = this.objectRemoval(
            oldTrainingUnit.childTrainingUnits,
            responseObject.id,
          );
        } else if (oldTrainingPeriod && oldTrainingPeriod.id !== targetTrainingPeriod?.id) {
          oldTrainingPeriod.trainingUnits = this.objectRemoval(oldTrainingPeriod.trainingUnits, responseObject.id);
        }
        if (targetTrainingUnit && targetTrainingUnit.id !== oldTrainingUnit?.id) {
          targetTrainingUnit.childTrainingUnits.push(this.modalObject);
        }
        if (!targetTrainingUnit && targetTrainingPeriod?.id !== oldTrainingPeriod?.id) {
          targetTrainingPeriod.trainingUnits.push(this.modalObject);
        }
      }
    },

    moveSubject(responseObject) {
      const trainingPeriod = this.getTrainingPeriod(this.modalTrainingPeriodId);
      const targetTrainingUnit = this.getTrainingUnit(trainingPeriod, responseObject.trainingUnit.id);
      const oldTrainingUnit = this.getTrainingUnit(trainingPeriod, this.modalParentUnitId);
      if (targetTrainingUnit && oldTrainingUnit && oldTrainingUnit.id !== targetTrainingUnit.id) {
        oldTrainingUnit.trainingSubjects = this.objectRemoval(oldTrainingUnit.trainingSubjects, responseObject.id);
        targetTrainingUnit.trainingSubjects.push(this.modalObject);
      }
    },

    moveToTrainingPeriod(responseObject) {
      const targetTrainingPeriod = this.getTrainingPeriod(responseObject.trainingPeriod.id);
      const oldTrainingPeriod = this.getTrainingPeriod(this.modalTrainingPeriodId);
      if (targetTrainingPeriod && oldTrainingPeriod && targetTrainingPeriod.id !== oldTrainingPeriod.id) {
        oldTrainingPeriod.trainingUnits = this.objectRemoval(oldTrainingPeriod.trainingUnits, responseObject.id);
        targetTrainingPeriod.trainingUnits.push(this.modalObject);
      }
    },

    getKeysToUpdate() {
      if (this.modalType === "training-period") {
        return ["name", "code"];
      } else if (this.modalType === "unit") {
        return [
          "parentTrainingUnit",
          "trainingPeriod",
          "name",
          "code",
          "ectsCredit",
          "coefficient",
          "mandatory",
          "totalHours",
          "type",
          "confidenceScore",
        ];
      } else if (this.modalType === "subject") {
        return [
          "trainingUnit",
          "name",
          "code",
          "ectsCredit",
          "coefficient",
          "mandatory",
          "totalHours",
          "type",
          "confidenceScore",
        ];
      }
      return [];
    },

    objectPush(response) {
      const responseObjet = this.getResponseObjet(response);
      if (responseObjet) {
        if (this.modalType === "training-period") {
          this.trainingPeriods.push(responseObjet);
        } else {
          if (this.modalType === "unit") {
            const trainingPeriod = this.getTrainingPeriod(responseObjet.trainingPeriod?.id);
            const parentTrainingUnit = this.getTrainingUnit(trainingPeriod, responseObjet.parentTrainingUnit?.id);
            if (trainingPeriod && !parentTrainingUnit) {
              if (!trainingPeriod.trainingUnits) {
                trainingPeriod.trainingUnits = [];
              }
              trainingPeriod.trainingUnits.push(responseObjet);
            } else if (parentTrainingUnit) {
              if (!parentTrainingUnit.childTrainingUnits) {
                parentTrainingUnit.childTrainingUnits = [];
              }
              parentTrainingUnit.childTrainingUnits.push(responseObjet);
            }
          } else if (this.modalType === "subject") {
            const trainingPeriod = this.getTrainingPeriod(responseObjet.trainingUnit?.trainingPeriod?.id);
            const trainingUnit = this.getTrainingUnit(trainingPeriod, responseObjet.trainingUnit?.id);
            if (trainingPeriod && trainingUnit) {
              if (!trainingUnit.trainingSubjects) {
                trainingUnit.trainingSubjects = [];
              }
              trainingUnit.trainingSubjects.push(responseObjet);
            }
          }
        }
      }
    },

    getTrainingPeriod(trainingPeriodId) {
      return Object.values(this.trainingPeriods).find((trainingPeriod) => trainingPeriod.id === trainingPeriodId);
    },

    getTrainingUnit(trainingPeriod, trainingUnitId) {
      let trainingUnit = Object.values(trainingPeriod.trainingUnits).find((unit) => unit.id === trainingUnitId);
      if (!trainingUnit) {
        Object.values(trainingPeriod.trainingUnits).forEach((unit) => {
          if (!trainingUnit) {
            trainingUnit = Object.values(unit.childTrainingUnits).find((unit) => unit.id === trainingUnitId);
          }
        });
      }
      return trainingUnit;
    },

    getChildTrainingUnit(parentTrainingUnit, trainingUnitId) {
      return Object.values(parentTrainingUnit.childTrainingUnits).find((unit) => unit.id === trainingUnitId);
    },

    clearModal() {
      this.models = [];
      this.modalTitle = "";
      this.modalType = "";
      this.modalTrainingPeriodId = null;
      this.modalObjectId = null;
      this.modalObject = null;
      this.modalLoadObject = null;
      this.modalAlertTitle = null;
      this.modalAlertContent = null;
    },

    hideComplete(payload) {
      if (payload.type === "training-period") {
        this.trainingPeriods = this.objectRemoval(this.trainingPeriods, payload.object.id);
      } else if (payload.type === "unit") {
        const trainingPeriod = this.getTrainingPeriod(payload.trainingPeriod?.id);
        if (!payload.parent && trainingPeriod) {
          trainingPeriod.trainingUnits = this.objectRemoval(trainingPeriod.trainingUnits, payload.object.id);
        } else if (trainingPeriod) {
          const parentTrainingUnit = this.getTrainingUnit(trainingPeriod, payload.parent?.id);
          if (parentTrainingUnit) {
            parentTrainingUnit.childTrainingUnits = this.objectRemoval(
              parentTrainingUnit.childTrainingUnits,
              payload.object.id,
            );
          }
        }
      } else if (payload.type === "subject") {
        const trainingPeriod = this.getTrainingPeriod(payload.trainingPeriod?.id);
        const trainingUnit = this.getTrainingUnit(trainingPeriod, payload.parent?.id);
        if (trainingUnit) {
          trainingUnit.trainingSubjects = this.objectRemoval(trainingUnit.trainingSubjects, payload.object.id);
        }
      }
    },

    removed(payload) {
      if (payload.type === "training-period") {
        const trainingPeriod = this.getTrainingPeriod(payload.object.id);
        if (trainingPeriod) {
          trainingPeriod.removed = true;
        }
      } else if (payload.type === "unit") {
        payload.object.removed = true;
      } else if (payload.type === "subject") {
        payload.object.removed = true;
      }
    },

    objectRemoval(object, id) {
      return object.filter((item) => id !== item.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.widget-content,
.widget-heading {
  @include up-to-md {
    padding: 20px 10px !important;
  }
}

.widget-heading {
  @include up-to-md {
    flex-direction: column;

    span.badge {
      display: block;
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }
}

.btn-icon-text {
  background: none;
  border: none;
  font-size: 14px;
  svg {
    margin-right: 5px;
  }
}

.btn-icon-text + .btn-icon-text {
  margin-left: 10px;
}

.widget-content {
  padding-bottom: 0 !important;
}

.btn-add {
  border: 1px solid $m_color_6;

  @include dark-theme {
    color: $white;
  }

  @include up-to-md {
    width: 100%;
  }

  &:hover {
    border: 1px solid $m_color_6;
  }
}

.btn-add + .btn-add {
  @include up-to-md {
    margin-top: 10px;
  }
}
</style>
